import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Config
import { uiConfig } from '../../../conf/securityPermissions';

// Components
import { AddEditPermission } from '../addEditPermission';
import Table from '../../../../components/Table';
import { Button } from '../../../../components/Button';
import { ConfirmationModal } from '../../../../components/ConfirmationModal';

// Hooks
import { usePermissionObjects } from '../../api/hooks/usePermissionObjects';

// Utils
import { translateUIConfigForTable, translateUIConfigForAddEdit } from '../../../../common/utility';

const { READ_ONLY_FEATURES } = process.env;

const TableContainer = styled.div`
    margin-left: -1rem;
    margin-right: -1rem;
`;

export const PermissionsTable = ({ data, sessionToken, onSave }) => {
    const permissionObjectsQuery = usePermissionObjects(sessionToken);
    const saveDisabled = READ_ONLY_FEATURES === 'true';
    const [editing, setEditing] = React.useState(null);
    const [deleting, setDeleting] = React.useState(null);
    const { columns, actions } = translateUIConfigForTable(uiConfig, setEditing, setDeleting);
    const items = React.useMemo(() => {
        return data?.securityPermissions ?? [];
    }, [data]);
    const controlButtons = [
        <Button key={'add-new'} appearance="primary" onClick={() => setEditing('new')}>
            Add Permission
        </Button>
    ];

    const selectedItem = React.useMemo(() => {
        if (!editing) return null;
        return (items ?? []).find((item) => item[uiConfig.primaryKey] === editing);
    }, [items, editing]);

    const closeAddEdit = () => {
        setEditing(null);
    };

    const tableData = React.useMemo(() => {
        if (!items) return [];
        const transformedItems =
            actions != null
                ? items.map((v) => ({
                      ...v,
                      permissionClass: permissionObjectsQuery?.data
                          ? permissionObjectsQuery?.data?.[v.permissionClass]
                          : v.permissionClass,
                      cascade: v?.cascade ? 'Yes' : 'No',
                      deny: v?.deny ? 'Yes' : 'No',
                      DataTableActions: actions.map((i) => {
                          return {
                              ...i,
                              action: () => i.action(v[uiConfig.primaryKey])
                          };
                      })
                  }))
                : items;
        return transformedItems.filter(({ dtoState }) => dtoState !== 'DELETED');
    }, [items, actions, permissionObjectsQuery?.data]);

    const deleteData = React.useMemo(() => {
        if (!deleting) return null;
        return items.find((item) => item[uiConfig.primaryKey] === deleting);
    }, [deleting]);

    const onDelete = (deletedPermission) => {
        const itemsCopy = [...items];
        const itemIndex = itemsCopy.findIndex(({ uuid }) => uuid === deletedPermission.uuid);

        if (itemIndex >= 0) {
            itemsCopy[itemIndex] = {
                ...deletedPermission,
                dtoState: 'DELETED'
            };
        }

        onSave({
            securityPermissions: itemsCopy,
            dtoState: 'UPDATED'
        });
    };

    const isExistingDoc = (doc) => {
        return (
            (items ?? []).find((v) => {
                return (
                    v.uuid !== doc.uuid &&
                    v.permissionClass === doc.permissionClass &&
                    v.permissionType === doc.permissionType
                );
            }) !== undefined
        );
    };

    return (
        <>
            <TableContainer>
                <Table
                    withHeader={true}
                    columns={columns}
                    data={tableData}
                    withActions={actions !== null}
                    id={'security-permissions-table'}
                    label={'Security Permissions'}
                    showControlDeck={true}
                    paginated={true}
                    controlButtons={controlButtons}
                />
            </TableContainer>
            {(editing != null || editing === 'new') && (
                <AddEditPermission
                    schemeId={data.schemeId}
                    isNew={editing === 'new'}
                    formModel={translateUIConfigForAddEdit(uiConfig)}
                    saveDisabled={saveDisabled}
                    data={selectedItem}
                    closeDialog={closeAddEdit}
                    sessionToken={sessionToken}
                    isExistingDoc={isExistingDoc}
                    onApply={(targetPermission) => {
                        const itemsCopy = [...items];
                        const itemIndex = itemsCopy.findIndex(({ uuid }) => uuid === targetPermission.uuid);

                        if (itemIndex >= 0) {
                            itemsCopy[itemIndex] = targetPermission;
                        } else {
                            itemsCopy.push(targetPermission);
                        }
                        onSave({
                            securityPermissions: itemsCopy,
                            dtoState: 'UPDATED'
                        });
                        if (editing === 'new') {
                            setEditing(targetPermission.uuid);
                        }
                    }}
                    onMount={typeof uiConfig.onFormMount === 'function' ? uiConfig.onFormMount : undefined}
                />
            )}
            {deleting != null && (
                <ConfirmationModal
                    autoHeight
                    header={`Delete Permission`}
                    body={'Are you sure you wish to delete this security permission?'}
                    confirmButtonText={'Delete'}
                    confirmButtonProps={{ appearance: 'destructive', text: true }}
                    closeDialog={() => setDeleting(null)}
                    onConfirm={() =>
                        new Promise((resolve) => {
                            onDelete(deleteData);
                            setDeleting(null);
                        })
                    }
                />
            )}
        </>
    );
};

export default PermissionsTable;

PermissionsTable.propTypes = {
    data: PropTypes.object,
    items: PropTypes.arrayOf(PropTypes.object),
    sessionToken: PropTypes.string,
    onSave: PropTypes.func
};
